import { getFirestore, collection, addDoc, updateDoc, doc, query, where, getDocs } from 'firebase/firestore';
import { logger } from '../utils/logger';
import i18next from 'i18next';

export interface NotificationData {
  type: 'certification_approved' | 'certification_rejected' | 'certification_pending' | 'worker_approved' | 'worker_rejected' | 'profile_photo_rejected' | 'identification_rejected' | 'address_rejected' | 'user_blocked';
  title: string;
  message: string;
  userId: string;
  certificationId?: string;
  certificationName?: string;
  reviewedBy?: string;
  reviewNotes?: string;
  createdAt: string;
  read: boolean;
}

export interface CertificationReviewData {
  certificationId: string;
  certificationName: string;
  previousStatus: string;
  newStatus: string;
  reviewedBy: string;
  reviewedAt: string;
  notes?: string;
  userId: string;
}

class NotificationService {
  private db = getFirestore();

  /**
   * Envía una notificación a un usuario específico
   */
  async sendNotification(notificationData: Omit<NotificationData, 'createdAt' | 'read'>): Promise<void> {
    try {
      const notification: NotificationData = {
        ...notificationData,
        createdAt: new Date().toISOString(),
        read: false
      };

      // Remover campos undefined o vacíos para evitar errores de Firestore
      const cleanNotification = Object.fromEntries(
        Object.entries(notification).filter(([_, value]) => 
          value !== undefined && value !== null && value !== ''
        )
      ) as NotificationData;

      await addDoc(
        collection(this.db, 'notifications', notificationData.userId, 'notifications'),
        cleanNotification
      );

      logger.info('NotificationService', 'Notificación enviada exitosamente', {
        userId: notificationData.userId,
        type: notificationData.type,
        title: notificationData.title
      });
    } catch (error) {
      logger.error('NotificationService', 'Error al enviar notificación', error);
      throw error;
    }
  }

  /**
   * Envía notificación de certificación aprobada
   */
  async sendCertificationApprovedNotification(
    userId: string,
    certificationId: string,
    certificationName: string,
    reviewedBy: string,
    reviewNotes?: string,
    userLanguage?: string
  ): Promise<void> {
    // Usar el idioma del usuario si se proporciona, de lo contrario usar el idioma actual
    const language = userLanguage || i18next.language;
    
    await this.sendNotification({
      type: 'certification_approved',
      title: i18next.t('notifications.certificationApproved', { lng: language }),
      message: i18next.t('notifications.certificationApprovedMessage', { 
        certificationName,
        lng: language 
      }),
      userId,
      certificationId,
      certificationName,
      reviewedBy,
      reviewNotes
    });
  }

  /**
   * Envía notificación de certificación rechazada
   */
  async sendCertificationRejectedNotification(
    userId: string,
    certificationId: string,
    certificationName: string,
    reviewedBy: string,
    reviewNotes?: string,
    userLanguage?: string
  ): Promise<void> {
    // Usar el idioma del usuario si se proporciona, de lo contrario usar el idioma actual
    const language = userLanguage || i18next.language;
    
    await this.sendNotification({
      type: 'certification_rejected',
      title: i18next.t('notifications.certificationRejected', { lng: language }),
      message: i18next.t('notifications.certificationRejectedMessage', { 
        certificationName, 
        reason: reviewNotes || i18next.t('notifications.noReasonProvided', { lng: language }),
        lng: language 
      }),
      userId,
      certificationId,
      certificationName,
      reviewedBy,
      reviewNotes
    });
  }

  /**
   * Envía notificación de worker aprobado
   */
  async sendWorkerApprovedNotification(
    userId: string,
    reviewedBy: string,
    reviewNotes?: string,
    userLanguage?: string
  ): Promise<void> {
    // Usar el idioma del usuario si se proporciona, de lo contrario usar el idioma actual
    const language = userLanguage || i18next.language;
    
    await this.sendNotification({
      type: 'worker_approved',
      title: i18next.t('notifications.workerApproved', { lng: language }),
      message: i18next.t('notifications.workerApprovedMessage', { lng: language }),
      userId,
      reviewedBy,
      reviewNotes
    });
  }

  /**
   * Envía notificación de worker rechazado
   */
  async sendWorkerRejectedNotification(
    userId: string,
    reviewedBy: string,
    reviewNotes?: string,
    userLanguage?: string
  ): Promise<void> {
    // Usar el idioma del usuario si se proporciona, de lo contrario usar el idioma actual
    const language = userLanguage || i18next.language;
    
    await this.sendNotification({
      type: 'worker_rejected',
      title: i18next.t('notifications.workerRejected', { lng: language }),
      message: i18next.t('notifications.workerRejectedMessage', { 
        reason: reviewNotes || i18next.t('notifications.noReasonProvided', { lng: language }),
        lng: language 
      }),
      userId,
      reviewedBy,
      reviewNotes
    });
  }

  /**
   * Envía notificación de foto de perfil rechazada
   */
  async sendProfilePhotoRejectedNotification(
    userId: string,
    reviewedBy: string,
    reviewNotes?: string,
    userLanguage?: string
  ): Promise<void> {
    // Usar el idioma del usuario si se proporciona, de lo contrario usar el idioma actual
    const language = userLanguage || i18next.language;
    
    await this.sendNotification({
      type: 'profile_photo_rejected',
      title: i18next.t('notifications.profilePhotoRejected', { lng: language }),
      message: i18next.t('notifications.profilePhotoRejectedMessage', { 
        reason: reviewNotes || i18next.t('notifications.noReasonProvided', { lng: language }),
        lng: language 
      }),
      userId,
      reviewedBy,
      reviewNotes
    });
  }

  /**
   * Envía notificación de identificación rechazada
   */
  async sendIdentificationRejectedNotification(
    userId: string,
    reviewedBy: string,
    reviewNotes?: string,
    userLanguage?: string
  ): Promise<void> {
    // Usar el idioma del usuario si se proporciona, de lo contrario usar el idioma actual
    const language = userLanguage || i18next.language;
    
    await this.sendNotification({
      type: 'identification_rejected',
      title: i18next.t('notifications.identificationRejected', { lng: language }),
      message: i18next.t('notifications.identificationRejectedMessage', { 
        reason: reviewNotes || i18next.t('notifications.noReasonProvided', { lng: language }),
        lng: language 
      }),
      userId,
      reviewedBy,
      reviewNotes
    });
  }

  /**
   * Envía notificación de dirección rechazada
   */
  async sendAddressRejectedNotification(
    userId: string,
    reviewedBy: string,
    reviewNotes?: string,
    userLanguage?: string
  ): Promise<void> {
    // Usar el idioma del usuario si se proporciona, de lo contrario usar el idioma actual
    const language = userLanguage || i18next.language;
    
    await this.sendNotification({
      type: 'address_rejected',
      title: i18next.t('notifications.addressRejected', { lng: language }),
      message: i18next.t('notifications.addressRejectedMessage', { 
        reason: reviewNotes || i18next.t('notifications.noReasonProvided', { lng: language }),
        lng: language 
      }),
      userId,
      reviewedBy,
      reviewNotes
    });
  }

  /**
   * Envía notificación de usuario bloqueado
   */
  async sendUserBlockedNotification(
    userId: string,
    blockedBy: string,
    blockReason?: string,
    userLanguage?: string
  ): Promise<void> {
    // Usar el idioma del usuario si se proporciona, de lo contrario usar el idioma actual
    const language = userLanguage || i18next.language;
    
    await this.sendNotification({
      type: 'user_blocked',
      title: i18next.t('userBlocked.title', { lng: language }),
      message: i18next.t('userBlocked.message', { 
        reason: blockReason || i18next.t('notifications.noReasonProvided', { lng: language }),
        lng: language 
      }),
      userId,
      reviewedBy: blockedBy,
      reviewNotes: blockReason
    });
  }

  /**
   * Marca una notificación como leída
   */
  async markNotificationAsRead(userId: string, notificationId: string): Promise<void> {
    try {
      await updateDoc(
        doc(this.db, 'notifications', userId, 'notifications', notificationId),
        { read: true }
      );
    } catch (error) {
      logger.error('NotificationService', 'Error al marcar notificación como leída', error);
      throw error;
    }
  }

  /**
   * Marca todas las notificaciones de un usuario como leídas
   */
  async markAllNotificationsAsRead(userId: string): Promise<void> {
    try {
      const notificationsRef = collection(this.db, 'notifications', userId, 'notifications');
      const q = query(notificationsRef, where('read', '==', false));
      const snapshot = await getDocs(q);
      
      const updatePromises = snapshot.docs.map(doc => 
        updateDoc(doc.ref, { read: true })
      );
      
      await Promise.all(updatePromises);
    } catch (error) {
      logger.error('NotificationService', 'Error al marcar todas las notificaciones como leídas', error);
      throw error;
    }
  }

  /**
   * Registra una revisión de certificación en el historial
   */
  async logCertificationReview(reviewData: CertificationReviewData): Promise<void> {
    try {
      // Remover campos undefined o vacíos para evitar errores de Firestore
      const cleanReviewData = Object.fromEntries(
        Object.entries(reviewData).filter(([_, value]) => 
          value !== undefined && value !== null && value !== ''
        )
      );

      await addDoc(collection(this.db, 'certificationReviews'), {
        ...cleanReviewData,
        createdAt: new Date().toISOString()
      });

      logger.info('NotificationService', 'Revisión de certificación registrada', {
        certificationId: reviewData.certificationId,
        userId: reviewData.userId,
        newStatus: reviewData.newStatus
      });
    } catch (error) {
      logger.error('NotificationService', 'Error al registrar revisión de certificación', error);
      throw error;
    }
  }
}

export const notificationService = new NotificationService(); 